<template>
  <b-tab v-if="showMainTab">
    <template #title>
      <b-icon
        class="mr-2"
        icon="info-circle-fill"
      />
      Kenmerken
    </template>
    <p class="mt-4">
      De belangrijkste kenmerken van het realisatieproces
    </p>

    <table class="table table-striped table-sm">
      <tbody>
        <tr v-if="case_ref">
          <th scope="row">
            Proceskenmerk
          </th>
          <td>{{ case_ref }}</td>
        </tr>
        <tr v-if="locationRef">
          <th scope="row">
            Locatiekenmerk
          </th>
          <td>{{ locationRef }}</td>
        </tr>
        <tr v-if="address">
          <th scope="row">
            Adres
          </th>
          <td>
            {{ address }}
            <br>
            <a
              target="_blank"
              :href="`https://www.google.com/maps/search/?api=1&amp;query=${encodeURIComponent(address)}`"
            >
              Open adres in Google Maps <b-icon icon="box-arrow-up-right" />
            </a>
          </td>
        </tr>
        <tr v-if="StatusLabel">
          <th scope="row">
            Status
          </th>
          <td>{{ StatusLabel }}</td>
        </tr>
        <tr v-if="StatusDuration && ! missingDurationInformation">
          <th scope="row">
            Datum laatste stap
          </th>
          <td :title="StatusDuration">
            {{ dateOfLastStep }}
          </td>
        </tr>
        <tr v-if="ChargePointOperator">
          <th scope="row">
            CPO
          </th>
          <td>{{ ChargePointOperator }}</td>
        </tr>
        <tr v-if="contractor">
          <th scope="row">
            Aannemer
          </th>
          <td>{{ contractor }}</td>
        </tr>
        <!-- park-n-charge specific...  -->
        <tr v-if="FinUnit">
          <th scope="row">
            Financiële Unit
          </th>
          <td>{{ FinUnit }}</td>
        </tr>
        <tr v-if="hasProcessType">
          <th scope="row">
            Procestype
          </th>
          <td>{{ processType }}</td>
        </tr>
        <tr v-if="showTags">
          <th scope="row">
            Tags
          </th>
          <td>
            <span v-if="currentTags.length === 0">Er zijn geen tags</span>
            <b-badge
              v-for="(tag,index) in currentTags"
              :key="`tag_${index}`"
              class="mr-1"
              variant="secondary"
            >
              {{ labelByUuid({ uuid: tag }) }}
            </b-badge>
          </td>
        </tr>
      </tbody>
    </table>

    <AutomatedViewsBtn
      class="mb-3"
      :uuid="uuid"
      :disabled="disabled"
    />

    <h5 v-if="(location && location.Docs ? location.Docs.ParkingSpots : []).length !== 0">
      Handmatig startdocument
    </h5>
    <FilesManager
      :uuid="uuid"
      :disabled="true"
      :locked="true"
      :wide="true"
      category="parkingspots"
      view="list"
      linklabel="download"
    />
    <template v-if="(location && location.Docs ? location.Docs.ParkingSpots : []).length === 0">
      <DocDownload :uuid="uuid" />
    </template>

    <div class="Views">
      <div v-if="record.hasFileOfCategory({ category: 'topview' })">
        <h5 class="mt-3">
          Boven aanzicht
        </h5>
        <FilesManager
          :uuid="uuid"
          :wide="true"
          category="topview"
          view="thumbnails"
          linklabel="download"
        />
      </div>
      <div v-if="record.hasFileOfCategory({ category: 'sideview' })">
        <h5 class="mt-3">
          Zij aanzicht
        </h5>
        <FilesManager
          :uuid="uuid"
          :wide="true"
          category="sideview"
          view="thumbnails"
          linklabel="download"
        />
      </div>
    </div>

    <slot />

    <RelatedRequests
      :record="record"
      class="mb-4"
    />

    <RealisationStatusPanel
      :key="componentKey"
      class="mt-4 mb-4"
      :uuid="uuid"
      @update-component="componentKey++"
    />

    <RealisationVKBStatus
      class="mt-4 mb-4"
      :uuid="uuid"
    />
  </b-tab>
</template>

<script>

// import FileUpload from '@/components/FileUpload'
import DocDownload from '@/components/realisation/DocDownload.vue'
import AutomatedViewsBtn from '@/components/realisation/AutomatedViewsBtn.vue'
import RealisationStatusPanel from '@/components/realisation/RealisationStatusPanel.vue'
import RealisationVKBStatus from '@/components/realisation/RealisationVKBStatus.vue'
import RelatedRequests from '@/components/realisation/RelatedRequests.vue'
import FilesManager from '@/components/files/FilesManager.vue'

import { mapGetters } from 'vuex'

import { formatPostalCode } from '@/helpers/string'
import { msDurationToString, msToDate } from '@/helpers/date'


export default {
  name: 'RealisationSidebarMainTab',
  components: {
    // FileUpload,
    RealisationStatusPanel,
    RealisationVKBStatus,
    DocDownload,
    AutomatedViewsBtn,
    RelatedRequests,
    FilesManager
},
  props: {
    uuid: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      componentKey: 1
    }
  },
  computed: {
    ...mapGetters('realisation', [
      'recordByUuid',
      'currentStep',
    ]),
    ...mapGetters('tenant', [
      'isCurrentTenant',
      'getEnabledTags',
      'getLabelsByTagUuid',
      'getSelectOptionLabelByValue',
      'getEnabledContractors',
    ]),
    ...mapGetters('user', [
      'hasRole',
    ]),
    ...mapGetters('chargingpoints', [
      'getChargingPointByUUID',
    ]),
    ...mapGetters('stakeholders', [
      'getStakeholderBySlug'
    ]),
    record() {
      return this.recordByUuid({
        uuid: this.uuid,
      }) || false
    },
    showMainTab() {
      return (
        process.env.VUE_APP_TENANT !== 'rvb'
        && this.record
        && ! this.record.isUnsavedRecord()
      )
    },
    case_ref() {
      return this.record ? this.record.case_ref : false
    },
    location() {
      try {
        return this.record.values.Location
      } catch (e) {
        return {
          Location: {},
        }
      }
    },
    locationRef() {
      try {
        return `${this.case_ref}-${this.location.version}`
      } catch (e) {
        return ''
      }
    },
    contractor () {

      let slug = this.record.getStakeholderSlugByCategory({
        category: 'contractor'
      })

      if (slug) {
        return this.getStakeholderBySlug({ slug })?.name || false
      }
      return false

      // if (this.location.Contractor) {
      //   return this.getEnabledContractors.find(contractor => contractor.uuid === this.location.Contractor)?.label
      // }
      // return false
    },
    // TODO: CBAPI - Files ...
    Docs() {
      try {
        return this.location.Docs || {}
      } catch (e) {
        return {}
      }
    },
    StatusLabel() {
      try {
        // TODO - CBAPI - Get from flow current step
        return this.record.getCurrentFlowStatus()?.label
      } catch (e) {
        return ''
      }
    },
    StatusDuration() {
      try {
        let duration = this.record.getCurrentStepDuraction()
        return msDurationToString({ ms: Date.now() - duration.start })
      } catch (e) {
        return ''
      }
    },
    missingDurationInformation() {
      let duration = this.record.getCurrentStepDuraction()
      return duration.start === 0 || (
        duration.finished && duration.end === 0
      )
    },
    dateOfLastStep() {
      try {
        let duration = this.record.getCurrentStepDuraction()
        return msToDate({ ms: duration.start })
      } catch (e) {
        return ''
      }
    },
    ChargePointOperator() {
      try {
        let slug = this.record.getStakeholderSlugByCategory({
          category: 'cpo'
        })

        if (slug) {
          return this.getStakeholderBySlug({ slug })?.name || false
        }
        return false
        // return this.record.values.Location.ChargePointOperator
      } catch (e) {
        return false
      }
    },
    address() {
      try {
        if (this.isCurrentTenant({ tenant: 'go-ral' })) {
          let locationUuid = this.record.getConnectedLocationUuid({ 
            category: 'selected'
          })

          let location = this.getChargingPointByUUID({
            uuid: locationUuid,
          })
          return location.getFormattedAddress()
        } else {
          let location = this.location.Location
          let number = (`${location.HouseNumber || ''} ${location.HouseNumberSuffix || ''}`).trim()
          return (`${location.StreetName || ''} ${number}, ${formatPostalCode(location.PostalCode || '')} ${location.CityName || ''}`).trim().trim(',')
        }
      } catch (e) {
        return false
      }
    },

    hasProcessType() {
      return this.record.hasProcessType()
    },
    processType() {
      return this.getSelectOptionLabelByValue({
        name: 'processtypes',
        value: this.record.getProcessType(),
      })
    },


    /**************************************************************************
     * TAGS
     */
    currentTags() {
      return this.record.getTags()
    },

    showTags() {
      return this.getEnabledTags.length || ( this.record && this.record.hasTags())
    },


    /**************************************************************************
     * FIN UNIT - park-n-charge specific
     */
    FinUnit() {
      try {
        return this.hasRole({ role: 'cpo' }) && this.isCurrentTenant({ tenant: 'park-n-charge' })
          ? this.record.values.Location.FinUnit
          : false
      } catch (e) {
        return false
      }
    },
  },
  methods: {
    labelByUuid({ uuid }) {
      return this.getLabelsByTagUuid[uuid] || 'Onbekende tag'
    },
  },
}
</script>


<style lang="scss">
.Views {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  > div {
    width: 48%;
  }
}
</style>
